/**
 * auth-cross-tab-sync — 同浏览器多 tab 共享 admin 会话（localStorage 为唯一事实源）
 *
 * Why：refresh token 一次性轮换、旧 sid 随轮换即撤销（其 access 同时失效）。各 tab 内存态互不同步时，
 * 一个 tab 续期后其他 tab 握着的 refresh / access 全部作废 → 它们下一次续期 401 → 被判会话失效强制登出，
 * 登出又把共享的 localStorage 清空，连带所有 tab 掉登录。
 *
 * How：
 * - `installAuthCrossTabSync`：storage 事件（他 tab 续期 / 登录 / 登出 / 换号）与 bfcache 恢复（期间收不到事件）
 *   时以 localStorage 当前值对齐本 tab；
 * - `syncAuthFromSharedStorage`：续期前后主动对齐一次（storage 事件异步送达，续期锁交接时可能尚未到）。
 * 一律读 localStorage 当前值而非事件携带的 newValue：事件可能晚于本 tab 自己更新的写入送达。
 * 采纳只改内存、不回写 localStorage（见 auth-store `applyWithoutPersisting`）。
 */

import { queryClient } from '~/lib/query-client';
import {
  AUTH_STORAGE_KEY,
  type PersistedAuthSnapshot,
  applyWithoutPersisting,
  readPersistedAuthSnapshot,
  useAuthStore,
} from '~/stores/auth-store';

/** 被动登出 / 换号只发生在本 tab 之外，留一条结构化日志供运营反馈「为何被登出」时排障 */
function logCrossTabAdoption(kind: 'logout' | 'new_session'): void {
  console.warn(JSON.stringify({ event: 'admin.auth.cross_tab_adopt', kind }));
}

/**
 * 把他 tab 写入的会话快照采纳进本 tab：
 * - 共享区已无会话 → 他 tab 登出：本 tab 清会话 + 清 query 缓存；
 * - 本 tab 在会话中且同账号 → 同会话续期：换 token、不动 epoch（在途请求可用新 access 重试）。
 *   同账号在他 tab 重新登录也走这里：重登不撤销旧 sid，旧请求只会在鉴权中间件 401（未执行）后用新 access 重放，
 *   审计按 actor 记、归属不变。快照缺 access（旧版本只落 refresh）时保留本 tab access，401 时自然续期，
 *   不因 access 置空让守卫卸载页面；
 * - 本 tab 未登录 / 换了账号 → 新会话：epoch 自增（旧请求不得 replay；守卫以 epoch 为 key 重挂页面）+ 清 query 缓存。
 */
export function adoptSharedAuthSnapshot(shared: PersistedAuthSnapshot): void {
  const current = useAuthStore.getState();
  if (
    shared.refreshToken === current.refreshToken &&
    shared.accessToken === current.accessToken &&
    JSON.stringify(shared.user) === JSON.stringify(current.user)
  ) {
    return;
  }

  if (!shared.refreshToken) {
    if (!current.refreshToken && !current.user) return;
    applyWithoutPersisting(() => current.clearSession());
    queryClient.clear();
    logCrossTabAdoption('logout');
    return;
  }

  const sameAccountSession =
    current.refreshToken !== null && current.user !== null && shared.user?.id === current.user.id;
  if (sameAccountSession) {
    applyWithoutPersisting(() =>
      useAuthStore.setState({
        accessToken: shared.accessToken ?? current.accessToken,
        refreshToken: shared.refreshToken,
        user: shared.user,
      }),
    );
    return;
  }

  applyWithoutPersisting(() =>
    useAuthStore.setState((s) => ({
      accessToken: shared.accessToken,
      refreshToken: shared.refreshToken,
      user: shared.user,
      sessionEpoch: s.sessionEpoch + 1,
    })),
  );
  queryClient.clear();
  logCrossTabAdoption('new_session');
}

/** 主动以 localStorage 当前值对齐本 tab（读不到 / 不可用时不动）。 */
export function syncAuthFromSharedStorage(): void {
  const shared = readPersistedAuthSnapshot();
  if (shared) adoptSharedAuthSnapshot(shared);
}

/** 安装跨 tab 对齐监听（应用启动时调用一次），返回卸载函数。 */
export function installAuthCrossTabSync(): () => void {
  const onStorage = (event: StorageEvent) => {
    // key=null 是 localStorage.clear()；只认本 key 的写入
    if (event.key === AUTH_STORAGE_KEY) syncAuthFromSharedStorage();
  };
  // 从 bfcache 恢复的页面错过了冻结期间的 storage 事件，内存可能落后于共享区
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) syncAuthFromSharedStorage();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener('pageshow', onPageShow);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('pageshow', onPageShow);
  };
}
